import { appendQueryParams, appendQueryString } from "@kluntje/js-utils/lib/url-helpers";
import { APIService, DebuggerService } from "@kluntje/services";
import { HOUR_IN_MS } from "Constants/dateConstants";

import {
  ArticleApiMediaItem,
  ArticleApiResponse,
  ArticleApiResponseItem,
  ArticleInformation,
  ArticleServiceQueryOptions,
  ArticleServiceResponse,
  SearchResultMediaItem,
} from "./ArticleService.types";

class ArticleService {
  private get defaultQueryString(): string {
    return "_embed=wp:featuredmedia&_fields=id,title,link,excerpt,_embedded:wp:featuredmedia,_links.wp:featuredmedia";
  }

  public async getArticles(options: ArticleServiceQueryOptions): Promise<ArticleServiceResponse> {
    try {
      const getQueryUrl = this.getQueryUrl(options);
      const response = await APIService.fetchResponse(getQueryUrl, {
        cacheOptions: {
          validFor: HOUR_IN_MS,
          storageType: "session",
        },
      });

      if (response === null || !response.ok) {
        throw new Error("Failed to fetch articles");
      }
      const resultCount = parseInt(response.headers.get("X-WP-Total") ?? "0", 10);
      const pageCount = parseInt(response.headers.get("X-Wp-totalpages") ?? "0", 10);
      const results = (await response.json()) as ArticleApiResponse;
      const articles = results.map(responseItem => this.getArticleInformation(responseItem));
      return {
        resultCount,
        pageCount,
        results: articles,
      };
    } catch (error) {
      DebuggerService.error("Error fetching articles", error);
      return {
        resultCount: 0,
        pageCount: 0,
        results: [],
      };
    }
  }

  private getArticleInformation(article: ArticleApiResponseItem): ArticleInformation {
    const { title, link, excerpt } = article;
    const mediaItem = article._embedded?.["wp:featuredmedia"]?.[0];
    const image = mediaItem ? this.getMediaItem(mediaItem) : undefined;

    return {
      title: title.rendered,
      description: excerpt.rendered,
      link,
      image,
    };
  }

  private getMediaItem(mediaItem: ArticleApiMediaItem): SearchResultMediaItem {
    const { alt_text, media_details } = mediaItem;
    const renditions: Record<number, string> = {};

    const sdeRenditions = [
      media_details.sizes["sde-xs"],
      media_details.sizes["sde-s"],
      media_details.sizes["sde-m"],
      media_details.sizes["sde-l"],
      media_details.sizes["sde-xl"],
    ].filter(rendition => rendition !== undefined);

    sdeRenditions.forEach(rendition => {
      if (rendition) {
        renditions[rendition.width] = rendition.source_url;
      }
    });

    return {
      alt: alt_text,
      renditions,
    };
  }

  private getQueryUrl(options: ArticleServiceQueryOptions): string {
    const { apiBaseUrl, page = 1, pageSize = 10 } = options;

    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("per_page", pageSize.toString());

    const defaultQueryUrl = appendQueryString(apiBaseUrl, this.defaultQueryString);

    return appendQueryParams(defaultQueryUrl, queryParams);
  }
}

export default new ArticleService();
